// Signal Boost — game data
// A deliberately different shape, same family as The Inner Citadel: no
// money, no business plan. The "asset" here is your voice and the way you
// structure what comes out of it — an instrument most people only ever
// play three notes on. Three stages: Dial Check (the five vocal dials) ->
// The Funnel (frameworks that turn rambling into a point) -> The Room
// (high-stakes moments where all of it gets tested live) -> Broadcast Report.

// ---------------------------------------------------------------------------
// STAGE 1 — DIAL CHECK: the five dials every trained voice learns to move
// on purpose. Eight real moments, each solved by turning one specific dial.
// ---------------------------------------------------------------------------
export const DIALS = [
  { id: "RATE", label: "Rate of Speech", icon: "⏱️" },
  { id: "VOLUME", label: "Volume", icon: "🔊" },
  { id: "PITCH", label: "Pitch & Melody", icon: "🎵" },
  { id: "TONALITY", label: "Tonality", icon: "🎭" },
  { id: "PAUSE", label: "The Pause", icon: "⏸️" },
];

export const DIAL_MOMENTS = [
  {
    id: "m1",
    text: "You've built the whole update toward one number — the one your boss actually needs — and you're one sentence away from saying it.",
    correct: "RATE",
    explain: "Slowing down right here works like a verbal highlighter — it tells the room \"this is the part to remember.\"",
  },
  {
    id: "m2",
    text: "You're presenting in a packed conference room with no mic, and people in the back keep leaning forward to hear you.",
    correct: "VOLUME",
    explain: "A 6–7 out of 10 isn't shouting — it's the baseline that reads as confidence instead of asking to be heard.",
  },
  {
    id: "m3",
    text: "Three minutes into your update, a few people start checking their phones — every sentence has landed on the exact same note since you started.",
    correct: "PITCH",
    explain: "That's the \"Monday melody.\" The siren technique — low to high, on purpose — breaks the pattern before boredom sets in.",
  },
  {
    id: "m4",
    text: "You're delivering disappointing news using all the right words, calmly and evenly — but nobody in the room looks like they believe you actually care.",
    correct: "TONALITY",
    explain: "The face is the remote control for tonality. If it doesn't move, the voice won't either, no matter how right the words are.",
  },
  {
    id: "m5",
    text: "You just asked the toughest question in the room, and the silence that follows makes you want to immediately answer it yourself.",
    correct: "PAUSE",
    explain: "That silence isn't dead air — it's the room doing the thinking you asked it to do. Let it sit.",
  },
  {
    id: "m6",
    text: "You're walking through three slides of background nobody really needed before you finally reach the one slide with real news.",
    correct: "RATE",
    explain: "Speeding through the low-stakes context — then slowing for the news — keeps the room's attention pointed at what matters.",
  },
  {
    id: "m7",
    text: "You're closing a pitch and want the room to feel the size of the win, not just hear the number out loud.",
    correct: "VOLUME",
    explain: "A confident lift in volume on the close is what makes a number land as a big deal instead of a status update.",
  },
  {
    id: "m8",
    text: "You're on a video call, and even though your face is animated off-camera, everything you say comes out sounding like you're reading a grocery list.",
    correct: "TONALITY",
    explain: "Tonality has to travel through the screen on purpose — the face-voice link doesn't fire automatically just because the camera's on.",
  },
];

// ---------------------------------------------------------------------------
// STAGE 2 — THE FUNNEL: four frameworks that turn a rambling thinking-out-
// loud process into a distilled message. Four scenarios, each solved by
// picking the framework actually built for that situation.
// ---------------------------------------------------------------------------
export const FRAMEWORKS = [
  { id: "PARA", label: "PARA", subtitle: "Point → Action → Result → Ask" },
  { id: "PAR", label: "PAR", subtitle: "Pause → Ask → Respond" },
  { id: "THREE_TWO_ONE", label: "3-2-1", subtitle: "Three steps, two types, one thing" },
  { id: "AMS", label: "AMS", subtitle: "Analogy, Metaphor, Simile" },
];

export const FUNNEL_SCENARIOS = [
  {
    id: "f1",
    text: "Your manager stops by your desk: \"Hey — how's the onboarding project going?\" She's got 90 seconds before her next meeting. You've got two weeks of context, three blockers, and one real win.",
    correct: "PARA",
    explain: "PARA compresses two weeks into four beats she can actually use: the Point (on track, one blocker), the Action you took, the Result, and the Ask — what you need from her, if anything.",
  },
  {
    id: "f2",
    text: "Mid-answer in a stakeholder meeting, someone cuts in with a sharp \"why would we even do it that way?\" — a question you weren't ready for and don't have a polished answer to.",
    correct: "PAR",
    explain: "PAR buys you a real 3–5 second Pause to think, an Ask (\"what's driving that question for you?\") to find what they're actually worried about, and only then a Respond aimed at the real concern instead of a guess.",
  },
  {
    id: "f3",
    text: "Your cousin at Thanksgiving asks what you actually do all day. She's expecting one sentence, not a monologue about your entire career.",
    correct: "THREE_TWO_ONE",
    explain: "The 3-2-1 framework's constraint — \"the one thing\" — forces you to distill an entire job down to a single sentence a non-expert can actually hold onto.",
  },
  {
    id: "f4",
    text: "You need to explain compound interest to someone who's never thought about money growing on its own, and \"exponential function\" isn't going to land.",
    correct: "AMS",
    explain: "AMS reaches for something they already understand — a snowball rolling downhill, gathering more snow with every turn — and lets that carry the idea instead of the math.",
  },
];

// ---------------------------------------------------------------------------
// STAGE 3 — THE ROOM: six high-stakes moments where habits, pressure, and
// presence all get tested at once. Each has one clean answer, one merely
// survivable one, and one that actively costs you credibility.
// ---------------------------------------------------------------------------
export const ROOM_ENCOUNTERS = [
  {
    id: "r1",
    title: "The Status Ask",
    text: "Your director asks, in front of three other people, \"Are we going to hit the deadline?\" You're confident, but there's one risk you haven't fully ruled out.",
    choices: [
      {
        id: "direct",
        label: "\"Yes, on track — one risk I'm watching is X, and I'll flag it by Thursday if that changes.\"",
        tag: "good",
        result: "Direct, honest, and it gave the room something concrete to track instead of a vague hedge.",
      },
      {
        id: "bare-yes",
        label: "Just say \"yes\" and move on, no detail offered",
        tag: "weak",
        result: "Technically answered — but you left her without the one detail she actually needed to hear.",
      },
      {
        id: "softener",
        label: "\"Um, I think, probably, yeah, I mean it should be fine I guess?\"",
        tag: "bad",
        result: "Every filler word and softener quietly told the room you don't trust your own answer — even though the plan was fine.",
      },
    ],
  },
  {
    id: "r2",
    title: "The Negotiation",
    text: "You're negotiating your first project budget with a vendor who opens aggressively low. Your instinct is to \"win\" by pushing back just as hard.",
    choices: [
      {
        id: "win-win",
        label: "Anchor on a number that works for both sides, then ask what would make a higher number work for them",
        tag: "good",
        result: "Win-win doesn't mean giving in — you found a number you could both defend, and the relationship survives the next negotiation too.",
      },
      {
        id: "cave",
        label: "Just accept their first number to avoid conflict",
        tag: "weak",
        result: "No friction, no fight — and you left real value on the table for no reason.",
      },
      {
        id: "match-aggression",
        label: "Match their aggression and refuse to move an inch",
        tag: "bad",
        result: "You \"won\" this round on paper. They stopped returning calls the next quarter.",
      },
    ],
  },
  {
    id: "r3",
    title: "The Camera Is On",
    text: "You're leading a virtual all-hands. Halfway through, you catch yourself glancing at your own video thumbnail instead of the lens, and your hands have been out of frame the whole call.",
    choices: [
      {
        id: "social-space",
        label: "Reframe from the waist up and look directly into the camera lens, not the screen",
        tag: "good",
        result: "Social space put your hands back in the conversation, and eye-to-lens contact read as real eye contact to everyone on the call.",
      },
      {
        id: "half-fix",
        label: "Fix the framing but keep glancing at your own thumbnail out of habit",
        tag: "weak",
        result: "Better framing, but the self-monitoring glances still read as distracted.",
      },
      {
        id: "ignore",
        label: "Keep going exactly as is — it's just a call, nobody really notices",
        tag: "bad",
        result: "Someone later mentioned it felt like you were talking to your notes the whole meeting, not to them.",
      },
    ],
  },
  {
    id: "r4",
    title: "The Question You Can't Answer",
    text: "Mid-presentation, someone asks a detailed question you genuinely don't know the answer to, and forty people are still waiting on the call.",
    choices: [
      {
        id: "take-offline",
        label: "\"I don't know off the top of my head — let me take that offline and follow up by Friday,\" then move on",
        tag: "good",
        result: "One honest sentence, and the presentation's momentum barely wobbled.",
      },
      {
        id: "stall",
        label: "Spend two minutes explaining everything you do know around the question without ever addressing it",
        tag: "weak",
        result: "You never technically admitted not knowing — but everyone on the call could tell you were stalling.",
      },
      {
        id: "guess",
        label: "Guess at an answer that sounds confident so the pause doesn't feel awkward",
        tag: "bad",
        result: "The guess was wrong, and now you're fielding a second question correcting the first one.",
      },
    ],
  },
  {
    id: "r5",
    title: "Level 5 Teaching Level 10",
    text: "A newer teammate asks you to explain a process you're still only half-confident in yourself. Your first thought is that you're not qualified to be the one explaining it.",
    choices: [
      {
        id: "teach-honestly",
        label: "Teach what you actually know clearly, and say plainly which parts you're still figuring out too",
        tag: "good",
        result: "Teaching it out loud sharpened your own understanding — and being honest about the gaps made you more credible, not less.",
      },
      {
        id: "over-hedge",
        label: "Explain it, but hedge nearly every sentence with \"I could be totally wrong but...\"",
        tag: "weak",
        result: "The information got through, but every hedge chipped away at how much they trusted it.",
      },
      {
        id: "deflect",
        label: "Deflect the question entirely because you don't feel like \"the expert\"",
        tag: "bad",
        result: "The teammate stayed stuck, and you stayed exactly as uncertain as before — avoiding it didn't make you any more ready.",
      },
    ],
  },
  {
    id: "r6",
    title: "Two Seconds",
    text: "A colleague finishes making a point in a meeting. You've got a sharper idea and can feel yourself wanting to jump in before they've even finished their sentence.",
    choices: [
      {
        id: "two-second-wait",
        label: "Wait two full seconds after they finish, ask one question about their point, then add yours",
        tag: "good",
        result: "The two-second gap made room for one more piece of their idea to surface — the piece that actually made yours better.",
      },
      {
        id: "pivot-to-self",
        label: "Wait for a full pause, but immediately pivot the conversation to your own story about a similar project",
        tag: "weak",
        result: "Technically didn't interrupt — but the conversation became about you instead of staying with their point.",
      },
      {
        id: "cut-in",
        label: "Jump in half a sentence early to get your point out before it's lost",
        tag: "bad",
        result: "You got your point out. You also talked over the actual insight they were about to add.",
      },
    ],
  },
];
